import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Switch,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DigitalIdentityTrustStatus,
  useUser_GetUserByIdQuery,
  useUser_UpdateUserDitStatusMutation,
} from 'queries';
import { X } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  onExitClick: () => void;
  id: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateListState: Function;
};

const useStyles = makeStyles((theme) => ({
  root: { textAlign: 'center' },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    margin: 'auto',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  title: {
    paddingBottom: '20px',
  },
  description: {
    width: '50%',
    marginBottom: '30px',
    fontWeight: 600,
  },
  buttonText: {
    textAlign: 'center',
    display: 'flex',
    fontWeight: 600,
  },
  media: {
    objectFit: 'scale-down',
  },
  dataList: {
    columns: 2,
    textAlign: 'start',
  },
  switchContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  switch: {
    marginLeft: 10,
    marginRight: 10,
    '&:disabled': {
      color: theme.palette.error.main,
    },
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'end',
  },
  saveButton: {
    float: 'left',
    width: 50,
    background: theme.palette.secondary.main,
    margin: 10,
  },
}));

const DitApprovalModal = ({ showModal, setShowModal, onExitClick, id, updateListState }: Props) => {
  const classes = useStyles();
  const [checked, setChecked] = useState(false);

  const handleOnChange = () => setChecked((status) => !status);
  const [updateUser, { loading: updateUserExecuting }] = useUser_UpdateUserDitStatusMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating user dit status: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: userData, loading: userLoading } = useUser_GetUserByIdQuery({
    variables: {
      id: id,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting users by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!userData || userLoading) {
    return <Spinner />;
  }

  const onSaveClick = () => {
    updateUser({
      variables: {
        input: {
          id: userData!.data.id,
          efxDigitalIdentityTrust: checked
            ? DigitalIdentityTrustStatus.Approve
            : DigitalIdentityTrustStatus.Deny,
          isConcierge: userData?.data.isConcierge ? true : false,
          isSuperAdmin: userData?.data.isSuperAdmin ? true : false,
        },
      },
    })
      .then(() => {
        updateListState(userData!.data.id);
        setChecked(false);
        onExitClick();
      })
      .catch(() => {
        console.log('There was an error saving changes to the selected user account.');
      });
  };

  return (
    <Dialog
      data-testid="dit-approval-modal"
      fullWidth={true}
      maxWidth="sm"
      open={showModal}
      transitionDuration={{ enter: 300, exit: 0 }}
      onClose={() => {
        setShowModal(false);
      }}
      className={classes.root}
    >
      <DialogTitle>
        <Typography className={classes.title} variant="h6">
          Verify Identity
        </Typography>
        <Typography variant="body1">
          Trustate asked Equifax to verify {userData?.data.firstName} {userData?.data.lastName} and
          received a DENY. This user requires manual identify verification before they can access
          Equifax results. If you trust {userData?.data.firstName} {userData?.data.lastName}, you
          can toggle their access below.
        </Typography>
        <List>
          <ListItem>
            Name: {userData?.data.firstName} {userData?.data.lastName}
          </ListItem>
          <ListItem>Email: {userData?.data.email}</ListItem>
          <ListItem>Phone: {userData?.data.phone}</ListItem>
        </List>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onExitClick}
          size="large"
        >
          <X />
        </IconButton>
      </DialogTitle>
      <div className={classes.switchContainer}>
        <Typography variant="body1" color="error">
          Deny
        </Typography>
        <Switch
          className={classes.switch}
          color="primary"
          checked={checked}
          onChange={handleOnChange}
        />
        <Typography variant="body1" color="primary">
          Allow
        </Typography>
      </div>
      <div className={classes.buttonContainer}>
        <Button className={classes.saveButton} onClick={onSaveClick} disabled={updateUserExecuting}>
          Save
        </Button>
      </div>
    </Dialog>
  );
};

export default DitApprovalModal;
